import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { orderAPI } from '../services/api';
import OrderCard from '../components/OrderCard';
import SearchBar from '../components/SearchBar';

const WorkerView = () => {
  const { selectedShop } = useOutletContext();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchActiveOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use a high limit to ensure we capture all active tasks without pagination cutting them off
      const response = await orderAPI.list({ shop: selectedShop, limit: 500 });
      
      // The backend returns { orders: [...], total: X }.
      // Spec: Cancelled and Delivered orders are NOT shown in Worker View
      const activeOrders = response.data.orders.filter(
        (o) => o.status !== 'Delivered' && o.status !== 'Cancelled'
      );
      
      setOrders(activeOrders);
    } catch (err) {
      setError('Failed to load active orders. Please check your connection.');
    } finally {
      setLoading(false);
    }
  }, [selectedShop]);

  // Re-fetch on shop change
  useEffect(() => {
    fetchActiveOrders();
  }, [fetchActiveOrders]);

  if (loading && orders.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin h-10 w-10 border-4 border-blue-600 rounded-full border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md mt-4 mx-4">
        <p className="text-red-700 font-medium">{error}</p>
      </div>
    );
  }

  // Safe date calculation using local string (YYYY-MM-DD) to prevent UTC shift bugs
  const today = new Date().toLocaleDateString('en-CA');

  // Segregation rules based strictly on Part 3 Spec
  const overdueOrders = orders.filter(o => o.is_overdue || (o.target_date < today));
  const dueTodayOrders = orders.filter(o => !o.is_overdue && o.target_date === today);
  const normalOrders = orders.filter(o => !o.is_overdue && o.target_date > today);

  return (
    <div className="max-w-3xl mx-auto space-y-8 pb-20">
      <SearchBar />
      
      {/* SECTION 1 — 🔴 OVERDUE */}
      <section>
        <h3 className="text-lg font-bold text-red-600 mb-4 flex items-center border-b border-red-200 pb-2">
          <span className="mr-2">🔴</span> OVERDUE
        </h3>
        {overdueOrders.length === 0 ? (
          <div className="bg-green-50 text-green-700 p-4 rounded-lg font-medium border border-green-200 shadow-sm flex items-center justify-center">
            No overdue orders <span className="ml-2">✅</span>
          </div>
        ) : (
          <div className="space-y-4">
            {overdueOrders.map(order => (
              <OrderCard key={order.order_id} order={order} onUpdate={fetchActiveOrders} />
            ))}
          </div>
        )}
      </section>

      {/* SECTION 2 — 🟡 DUE TODAY (Hidden entirely if empty) */}
      {dueTodayOrders.length > 0 && (
        <section>
          <h3 className="text-lg font-bold text-yellow-600 mb-4 flex items-center border-b border-yellow-200 pb-2">
            <span className="mr-2">🟡</span> DUE TODAY
          </h3>
          <div className="space-y-4">
            {dueTodayOrders.map(order => (
              <OrderCard key={order.order_id} order={order} onUpdate={fetchActiveOrders} />
            ))}
          </div>
        </section>
      )}

      {/* SECTION 3 — 🟢 NORMAL */}
      <section>
        <h3 className="text-lg font-bold text-green-600 mb-4 flex items-center border-b border-green-200 pb-2">
          <span className="mr-2">🟢</span> NORMAL
        </h3>
        {normalOrders.length === 0 ? (
          <p className="text-gray-500 italic px-2">No upcoming orders.</p>
        ) : (
          <div className="space-y-4">
            {normalOrders.map(order => (
              <OrderCard key={order.order_id} order={order} onUpdate={fetchActiveOrders} />
            ))}
          </div>
        )}
      </section>

    </div>
  );
};

export default WorkerView;